/**
 * Earcons (plan_real_guide_gaps.md, workstream V): a two-sound vocabulary,
 * synthesized in WebAudio — no assets, no decode, no library.
 *
 *   cardArrival — soft ascending two-note motif: "something's waiting for
 *                 you, glance at your phone". Fired when a primary action/
 *                 decision card lands.
 *   success     — short resolved arpeggio when an objective completes: the
 *                 tour's reward loop.
 *
 * Two rules keep this from becoming a toy:
 *   1. Gated on voice mode (no separate setting): voice mode on = the visitor
 *      opted into an audible experience; off = the app stays fully silent.
 *   2. Ducked under narration: a card arriving mid-sentence is announced by
 *      the sentence (that's the voice-mode prompt contract) — the earcon only
 *      fires into silence.
 *
 * A marimba hit is approximated as a sine fundamental plus a quiet 4th
 * harmonic (the woody strike), instant attack, fast exponential decay.
 */

import VoicePlaybackService from './VoicePlaybackService';

let ctx = null;
let enabled = false;
let resumeGestureAttached = false;

function getCtx() {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch (_) {
    return null;
  }
  return ctx;
}

// Autoplay policies may leave the context suspended until a user gesture.
// Earcons are disposable (a dropped ding is no tragedy), so we don't queue —
// we just make sure the NEXT one works.
function attachResumeGesture() {
  if (resumeGestureAttached || typeof document === 'undefined') return;
  resumeGestureAttached = true;
  document.addEventListener(
    'pointerdown',
    () => {
      resumeGestureAttached = false;
      try {
        const c = getCtx();
        if (c && c.state === 'suspended') c.resume().catch(() => {});
      } catch (_) {}
    },
    { once: true, capture: true }
  );
}

/** One marimba strike. Quiet by design (~-18dB relative to speech). */
function strike(c, freq, at, { peak = 0.12, decay = 0.45 } = {}) {
  const t = c.currentTime + at;

  const fundamental = c.createOscillator();
  fundamental.type = 'sine';
  fundamental.frequency.value = freq;

  // The "woody" attack: a 4th-harmonic partial that dies much faster.
  const partial = c.createOscillator();
  partial.type = 'sine';
  partial.frequency.value = freq * 4;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + decay);

  const partialGain = c.createGain();
  partialGain.gain.setValueAtTime(0.0001, t);
  partialGain.gain.exponentialRampToValueAtTime(peak * 0.25, t + 0.005);
  partialGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);

  fundamental.connect(gain).connect(c.destination);
  partial.connect(partialGain).connect(c.destination);
  fundamental.start(t);
  partial.start(t);
  fundamental.stop(t + decay + 0.05);
  partial.stop(t + 0.2);
}

const MOTIFS = {
  // E5 → A5, gentle "look here".
  cardArrival: [
    { freq: 659.25, at: 0 },
    { freq: 880.0, at: 0.11 },
  ],
  // C5 → E5 → G5, the classic resolved "done!".
  success: [
    { freq: 523.25, at: 0 },
    { freq: 659.25, at: 0.09 },
    { freq: 783.99, at: 0.18, peak: 0.15, decay: 0.6 },
  ],
};

function playMotif(name, { force = false, duck = true } = {}) {
  if (!enabled && !force) return;
  // Duck rule: narration owns the audio channel. 'loading' counts — an
  // utterance is already on its way for this turn.
  if (duck && !force) {
    try {
      if (VoicePlaybackService.getState().status !== 'idle') return;
    } catch (_) {}
  }
  const c = getCtx();
  if (!c) return;
  const notes = MOTIFS[name];
  if (!notes) return;
  const schedule = () => {
    try {
      notes.forEach(({ freq, at, peak, decay }) =>
        strike(c, freq, at, { ...(peak ? { peak } : {}), ...(decay ? { decay } : {}) })
      );
    } catch (_) {}
  };
  if (c.state === 'suspended') {
    c.resume()
      .then(() => {
        if (c.state === 'running') schedule();
      })
      .catch(() => attachResumeGesture());
    return;
  }
  schedule();
}

const SoundEffectsService = {
  /** Wired to voice mode by ChatSection — the single on/off for all earcons. */
  setEnabled(value) {
    enabled = value === true;
  },

  /** A primary action/decision card landed into silence. */
  cardArrival() {
    playMotif('cardArrival');
  },

  /** An objective was completed. A short quiet reward layers fine over
   *  speech (game-audio convention), so this one does NOT duck — ducking it
   *  swallowed the reward whenever the previous narration was still talking. */
  success() {
    playMotif('success', { duck: false });
  },

  /** Manual testing/demo from the console — bypasses voice-mode gate and
   *  ducking: window.__okioSfx.preview('cardArrival' | 'success') */
  preview(name) {
    playMotif(name, { force: true });
  },
};

if (typeof window !== 'undefined') {
  window.__okioSfx = SoundEffectsService;
}

export default SoundEffectsService;
